export type CopilotLatencyMetadata = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface CopilotLatencyEvent extends CopilotLatencyMetadata {
  phase: string;
  elapsed_ms: number;
  duration_ms?: number;
}

export interface CopilotLatencyReport {
  trace_id: string;
  interaction: 'message' | 'answer';
  terminal_status: string;
  total_duration_ms: number;
  phases: CopilotLatencyEvent[];
}

declare global {
  interface Window {
    /** Development-only, content-free Copilot timing reports (newest last). */
    __copilotLatencyTraces?: CopilotLatencyReport[];
  }
}

const MAX_RETAINED_TRACES = 20;
const SESSION_STORAGE_KEY = 'copilotLatency:last';
const MAX_LABEL_LENGTH = 64;
const MAX_IDENTIFIER_LENGTH = 128;
const SAFE_METADATA_KEYS = new Set([
  'attempt',
  'blocking_error_count',
  'count',
  'correction_round',
  'error_count',
  'is_valid',
  'operation_count',
  'outcome_kind',
  'step_count',
  'step_index',
  'stream_event_type',
  'tool_name',
  'validation_code',
]);

function roundMs(value: number): number {
  return Math.round(value * 100) / 100;
}

function boundedLabel(value: string, maxLength = MAX_LABEL_LENGTH): string {
  return value.replace(/[\r\n]/g, ' ').slice(0, maxLength);
}

function safeMetadata(
  metadata: CopilotLatencyMetadata
): CopilotLatencyMetadata {
  return Object.fromEntries(
    Object.entries(metadata).flatMap(([key, value]) => {
      if (!SAFE_METADATA_KEYS.has(key) || value === undefined) return [];
      return [
        [key, typeof value === 'string' ? boundedLabel(value) : value] as const,
      ];
    })
  );
}

/**
 * Request-local browser timing recorder. Metadata must stay content-free:
 * phase names, counts, safe tool names, and statuses only.
 */
export class CopilotLatencyTrace {
  private readonly startedAt: number;
  private readonly phases: CopilotLatencyEvent[] = [];
  private report: CopilotLatencyReport | null = null;

  constructor(
    private readonly traceId: string,
    private readonly interaction: 'message' | 'answer',
    private readonly now: () => number = () => performance.now()
  ) {
    this.startedAt = this.now();
  }

  mark(phase: string, metadata: CopilotLatencyMetadata = {}): void {
    if (this.report) return;
    this.phases.push({
      phase: boundedLabel(phase),
      elapsed_ms: roundMs(this.now() - this.startedAt),
      ...safeMetadata(metadata),
    });
  }

  startPhase(phase: string, metadata: CopilotLatencyMetadata = {}): () => void {
    const phaseStartedAt = this.now();
    let ended = false;
    return () => {
      if (ended || this.report) return;
      ended = true;
      const endedAt = this.now();
      this.phases.push({
        phase: boundedLabel(phase),
        elapsed_ms: roundMs(endedAt - this.startedAt),
        duration_ms: roundMs(endedAt - phaseStartedAt),
        ...safeMetadata(metadata),
      });
    };
  }

  finish(terminalStatus: string): CopilotLatencyReport {
    if (this.report) return this.report;
    this.report = {
      trace_id: boundedLabel(this.traceId, MAX_IDENTIFIER_LENGTH),
      interaction: this.interaction,
      terminal_status: boundedLabel(terminalStatus),
      total_duration_ms: roundMs(this.now() - this.startedAt),
      phases: [...this.phases],
    };

    if (
      process.env.NODE_ENV !== 'production' &&
      typeof window !== 'undefined'
    ) {
      const retained = [
        ...(window.__copilotLatencyTraces ?? []),
        this.report,
      ].slice(-MAX_RETAINED_TRACES);
      window.__copilotLatencyTraces = retained;
      try {
        window.sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify(this.report)
        );
      } catch {
        // Storage can be unavailable in hardened/private browser contexts.
      }
      try {
        console.info('[Copilot latency]', this.report);
      } catch {
        // Diagnostics must never affect the Copilot interaction.
      }
    }
    return this.report;
  }
}
